<!-- disableModelSelection — таблица выбора модели скрыта; юзер попадает
     сразу в тестер (см. autoOpenTesterVersionId). Полезно когда модель
     уже выбрана в родителе и нужно менять только voice/params. -->
<div class="model-selector-container" [hidden]="disableModelSelection">
  @if (hierarchyLoading) {
    <div class="hierarchy-loading-overlay">
      <p-progressSpinner styleClass="hierarchy-loading-spinner" strokeWidth="3" ariaLabel="Loading models" />
    </div>
  }
  <!-- Filters -->
  <div class="filters-section">
    <div class="filter-field">
      <label i18n="@@modelSelector.gateway">Gateway</label>
      <p-select
        [(ngModel)]="gatewayFilter"
        [options]="gatewayOptions"
        [disabled]="gatewayOptions.length === 1"
        (onChange)="applyFilters()"
        i18n-placeholder="@@modelSelector.selectGateway"
        placeholder="Select gateway"
        styleClass="w-full">
      </p-select>
    </div>

    <div class="filter-field">
      <label i18n="@@modelSelector.author">Author</label>
      <p-select
        [(ngModel)]="authorFilter"
        [options]="authorOptions"
        [disabled]="authorOptions.length === 1"
        (onChange)="applyFilters()"
        optionLabel="label"
        optionValue="value"
        i18n-placeholder="@@modelSelector.allAuthors"
        placeholder="All authors"
        styleClass="w-full">
      </p-select>
    </div>

    @if (modelTypeOptions.length > 1) {
      <div class="filter-field">
        <label i18n="@@modelSelector.modelType">Model Type</label>
        <p-select
          [(ngModel)]="modelTypeFilter"
          [options]="modelTypeOptions"
          (onChange)="applyFilters()"
          optionLabel="label"
          optionValue="value"
          i18n-placeholder="@@modelSelector.allTypes"
          placeholder="All types"
          styleClass="w-full">
        </p-select>
      </div>
    }

    <div class="filter-field">
      <label i18n="@@modelSelector.modelName">Model</label>
      <input
        pInputText
        type="text"
        [(ngModel)]="modelNameFilter"
        (input)="applyFilters()"
        i18n-placeholder="@@modelSelector.searchByName"
        placeholder="Search by model or version name"
        class="w-full">
    </div>

    <div class="filter-field filter-field-toggle">
      <label i18n="@@modelSelector.recommendedOnlyLabel">Favorites</label>
      <p-toggleSwitch
        [(ngModel)]="recommendedOnlyModel"
        (ngModelChange)="onRecommendedOnlyToggle($event)">
      </p-toggleSwitch>
    </div>

    @if (multiSelect) {
      <div class="filter-field">
        <label i18n="@@modelSelector.selected">Selected</label>
        <p-select
          [(ngModel)]="selectedOnlyFilter"
          [options]="selectedFilterOptions"
          (onChange)="applyFilters()"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full">
        </p-select>
      </div>
    }
  </div>

  <!-- Models tree table -->
  <div class="tree-table-wrap">
  <p-treeTable
    [value]="filteredTree"
    [scrollable]="true"
    scrollHeight="480px"
    [virtualScroll]="useVirtualScroll"
    [virtualScrollItemSize]="90"
    styleClass="p-treetable-sm">

    <!-- Colgroup: PrimeNG применяет эти <col> ширины и к header'у, и к
         virtual-scroll body-таблице — единственный корректный способ
         синхронизировать layout между двумя <table>-элементами scrollable
         + virtualScroll. Меняем через inline-стили, без CSS-хаков. -->
    <ng-template pTemplate="colgroup">
      <colgroup>
        <col>
        <col style="width: 200px">
        @if (multiSelect) {
          <col style="width: 110px">
        }
      </colgroup>
    </ng-template>

    <ng-template pTemplate="header">
      <tr>
        <th i18n="@@modelSelector.model">Модель</th>
        <th i18n="@@modelSelector.author">Автор</th>
        @if (multiSelect) {
          <th style="text-align: center;" i18n="@@modelSelector.action">Действие</th>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-rowNode let-rowData="rowData">
      <tr [ttRow]="rowNode"
          class="tree-row"
          [class.type-row]="rowData.kind === 'type'"
          [class.gateway-row]="rowData.kind === 'gateway'"
          [class.model-row]="rowData.kind === 'model'"
          [class.selected-row]="rowData.kind === 'model' && isModelSelected(rowData.row)"
          (click)="onTreeRowClick(rowData)">

        <!-- Column 1: Иерархия + Модель детали. Обёртка tree-cell/tree-cell-content
             нужна для того, чтобы pricing/benchmark блоки отступали ВПРАВО от
             toggler'а (иначе они блочные div'ы и уезжают к левому краю <td>). -->
        <td>
          <div class="tree-cell">
            <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
            <div class="tree-cell-content">
          @if (rowData.kind === 'type') {
            <i [class]="'pi ' + getModelTypeIcon(rowData.modelTypeCode)"
               [pTooltip]="getModelTypeTooltip(rowData.modelTypeCode)"
               style="margin-right: 0.5rem; color: var(--p-primary-color);"></i>
            <strong>{{ rowData.label }}</strong>
          } @else if (rowData.kind === 'gateway') {
            <span class="gateway-label">{{ rowData.label }}</span>
          } @else if (rowData.kind === 'model' && rowData.row; as row) {
            @if (row.isRecommended) {
              <i class="pi pi-star-fill" style="margin-right: 0.5rem; color: var(--p-yellow-500);" [pTooltip]="recommendedLabel"></i>
            }
            {{ row.modelName }}
            <!-- Автор дублируется сюда для мобилы: там колонка «Автор»
                 скрыта (на 390px две колонки не помещаются — см. CSS). -->
            <span class="author-inline show-mobile">{{ row.authorName || '—' }}</span>
            @if (row.hostingLocale; as hl) {
              <p-tag
                class="hosting-locale-tag"
                severity="secondary"
                [value]="(hl.flag ? hl.flag + ' ' : '') + hl.code"
                [pTooltip]="hostingLocaleTooltip(hl)"
                tooltipPosition="top">
              </p-tag>
            }
            @if (latestVersionForRow(row); as latest) {
              @if (hasPricingToShow(row, latest)) {
                <div class="pricing-snapshot-inline">
                  @if (isLlmRow(row)) {
                    @if (latest.inputPricePerMTokens) {
                      <span class="price-line"
                            i18n-pTooltip="@@modelSelector.price.tooltip.inputTokens"
                            pTooltip="Input price per million tokens (latest version)"
                            tooltipPosition="top">
                        <i class="pi pi-arrow-up"></i>
                        <span>{{ formatPerMTokens(latest.inputPricePerMTokens) }}</span>
                      </span>
                    }
                    @if (latest.outputPricePerMTokens) {
                      <span class="price-line"
                            i18n-pTooltip="@@modelSelector.price.tooltip.outputTokens"
                            pTooltip="Output price per million tokens (latest version)"
                            tooltipPosition="top">
                        <i class="pi pi-arrow-down"></i>
                        <span>{{ formatPerMTokens(latest.outputPricePerMTokens) }}</span>
                      </span>
                    }
                  } @else if (isTtsRow(row) && latest.pricePerMCharacters) {
                    <span class="price-line"
                          i18n-pTooltip="@@modelSelector.price.tooltip.characters"
                          pTooltip="Price per million characters (latest version)"
                          tooltipPosition="top">
                      <i class="pi pi-pencil"></i>
                      <span>{{ formatPerMCharacters(latest.pricePerMCharacters) }}</span>
                    </span>
                  } @else if (isSttRow(row) && latest.pricePerMinute) {
                    <span class="price-line"
                          i18n-pTooltip="@@modelSelector.price.tooltip.minute"
                          pTooltip="Price per minute of audio (latest version)"
                          tooltipPosition="top">
                      <i class="pi pi-clock"></i>
                      <span>{{ formatPerMinute(latest.pricePerMinute) }}</span>
                    </span>
                  }
                </div>
              }
            }
            @if (bestBenchmarkForRow(row); as best) {
              @if (best.latestBenchmark; as snap) {
                <div class="benchmark-snapshot-inline">
                  <span class="bench-line"
                        i18n-pTooltip="@@modelSelector.benchmark.tooltip.recordedAt"
                        pTooltip="Date of the last benchmark run"
                        tooltipPosition="top">
                    <i class="pi pi-clock"></i>
                    <span>{{ formatBenchmarkAt(snap.recorded_at) || '—' }}</span>
                  </span>
                  @if (bestBenchmarkTypeCode(row) === 'llm') {
                    <span class="bench-line"
                          i18n-pTooltip="@@modelSelector.benchmark.tooltip.tokensPerSec"
                          pTooltip="Average generation speed (tokens per second) across the benchmark"
                          tooltipPosition="top">
                      <i class="pi pi-gauge"></i>
                      <span>{{ formatTokensPerSec(snap.avg_tokens_per_sec) }}</span>
                    </span>
                    <span class="bench-line"
                          i18n-pTooltip="@@modelSelector.benchmark.tooltip.ttft"
                          pTooltip="Average time to first token across the benchmark"
                          tooltipPosition="top">
                      <i class="pi pi-bolt"></i>
                      <span>{{ formatTtftMs(snap.avg_ttft_ms) }}</span>
                    </span>
                  }
                  @if (bestBenchmarkTypeCode(row) === 'stt') {
                    <span class="bench-line"
                          i18n-pTooltip="@@modelSelector.benchmark.tooltip.wer"
                          pTooltip="Word Error Rate — доля ошибок распознавания в словах, ниже — лучше"
                          tooltipPosition="top">
                      <i class="pi pi-percentage"></i>
                      <span>WER {{ formatErrorRate(snap.wer) }}</span>
                    </span>
                    <span class="bench-line"
                          i18n-pTooltip="@@modelSelector.benchmark.tooltip.cer"
                          pTooltip="Character Error Rate — доля ошибок в символах"
                          tooltipPosition="top">
                      <i class="pi pi-percentage"></i>
                      <span>CER {{ formatErrorRate(snap.cer) }}</span>
                    </span>
                    <span class="bench-line"
                          i18n-pTooltip="@@modelSelector.benchmark.tooltip.rtfx"
                          pTooltip="RTFx — скорость обработки относительно длительности аудио"
                          tooltipPosition="top">
                      <i class="pi pi-bolt"></i>
                      <span>{{ formatRtfx(snap.avg_rtfx) }}</span>
                    </span>
                  }
                </div>
              }
            }
          }
            </div>
          </div>
        </td>

        <!-- Column 2: Автор -->
        <td>
          @if (rowData.kind === 'model' && rowData.row) {
            {{ rowData.row.authorName || '—' }}
          }
        </td>

        <!-- Column 3: Действие (multiSelect only, model rows only) -->
        @if (multiSelect) {
          <td style="text-align: center;" (click)="$event.stopPropagation()">
            @if (rowData.kind === 'model' && rowData.row && isModelSelected(rowData.row)) {
              <p-button
                icon="pi pi-times"
                severity="secondary"
                size="small"
                [pTooltip]="deselectLabel"
                tooltipPosition="left"
                [rounded]="true"
                [text]="true"
                (click)="deselectRow(rowData.row)"
                [ariaLabel]="deselectLabel">
              </p-button>
            }
          </td>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="multiSelect ? 3 : 2" class="text-center" i18n="@@modelSelector.empty">No models found</td>
      </tr>
    </ng-template>
  </p-treeTable>
  </div>

  <div class="dialog-actions">
    <p-button
      i18n-label="@@common.cancel"
      label="Cancel"
      severity="secondary"
      [outlined]="true"
      (click)="cancel()">
    </p-button>
    @if (multiSelect) {
      <p-button
        i18n-label="@@modelSelector.ok"
        label="OK"
        (click)="confirmSelection()">
      </p-button>
    }
  </div>
</div>

<!-- Tester drawer.
     appendTo="body" — обязательно. Если оставить дефолт 'self', контейнер
     drawer'а остаётся ИНЛАЙНОМ в текущем месте template'а. Когда этот
     model-selector отрендерен внутри другого модального drawer'а
     (напр. conversation-detail'овский synth-drawer), tester-панель
     оказывается в его stacking-контексте, а mask tester'а всегда идёт
     на body с более высоким z-index'ом → mask перекрывает всё, включая
     tester-панель, и клики не проходят. Body-mount разводит и панель,
     и mask по body, autoZIndex стекается корректно. -->
<p-drawer
  [(visible)]="testDrawerVisible"
  (onHide)="closeTester()"
  position="right"
  styleClass="responsive-drawer model-tester-drawer"
  [style]="{ width: '800px' }"
  appendTo="body">
  <ng-template pTemplate="header">
    @if (testContext) {
      <div class="drawer-header">
        <i [class]="'pi ' + getModelTypeIcon(testContext.modelTypeCode)" style="margin-right: 0.5rem; color: var(--p-primary-color);"></i>
        <span>{{ testContext.headerLine }}</span>
        <small class="drawer-subheader">{{ testContext.subHeader }}</small>
      </div>
    }
  </ng-template>
  @if (testContext) {
    <app-model-tester
      [modelTypeCode]="testContext.modelTypeCode"
      [versionId]="testContext.versionId"
      [versions]="testContext.versions"
      [versionLocked]="testContext.versionLocked"
      [additionalProperties]="testContext.additionalProperties"
      [currentValues]="testContext.currentValues"
      [currentVoiceId]="testContext.currentVoiceId"
      [currentTemperature]="testContext.currentTemperature"
      [currentTopK]="testContext.currentTopK"
      [currentTopP]="testContext.currentTopP"
      [currentMaxOutputTokens]="testContext.currentMaxOutputTokens"
      [allowCustomLlm]="!!testContext.allowCustomLlm"
      [currentCustomHeadersSchema]="testContext.currentCustomHeadersSchema"
      [currentCustomBodySchema]="testContext.currentCustomBodySchema"
      [defaultText]="defaultFirstMessage"
      [languageId]="languageId"
      [localeId]="localeId"
      [allowSelect]="allowSelectFromTester"
      [showPropertiesEdit]="showPropertiesEdit"
      (versionSelected)="onTesterVersionSelected($event)"
      (propertiesEditRequested)="onTesterPropertiesEditRequested($event)">
    </app-model-tester>
  }
  <ng-template pTemplate="footer">
    @if (allowSelectFromTester && testContext) {
      <div class="tester-drawer-footer">
        @if (multiSelect && isVersionSelected(activeTesterVersionId())) {
          <p-button
            i18n-label="@@modelTester.deselectVersion"
            label="Deselect this version"
            icon="pi pi-times"
            severity="secondary"
            size="large"
            (click)="deselectActiveTesterVersion()"
            styleClass="w-full">
          </p-button>
        } @else {
          <p-button
            i18n-label="@@modelTester.selectVersion"
            label="Select this version"
            icon="pi pi-check"
            size="large"
            [disabled]="!canCommitTesterSelection"
            (click)="commitTesterSelection()"
            styleClass="w-full">
          </p-button>
        }
      </div>
    }
  </ng-template>
</p-drawer>

<!-- Admin properties drawer: same shared mounting story. -->
<app-model-properties-drawer
  [visible]="propertiesDrawerVisible"
  [request]="propertiesRequest"
  (visibleChange)="onPropertiesDrawerVisibleChange($event)"
  (saved)="onPropertiesSaved($event)">
</app-model-properties-drawer>

<p-confirmDialog [style]="{ width: '480px', maxWidth: '90vw' }"></p-confirmDialog>
